/**
 * Password Strength Indicator Component
 * 
 * Visual indicator showing password strength with colored bars and message.
 * 
 * @module pages/auth/components
 */

import { getPasswordStrength, type PasswordStrength } from '~/lib/utils/passwordValidation';

export interface PasswordStrengthIndicatorProps {
  /** Password to evaluate */
  password: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Get color classes for strength level
 */
function getStrengthColor(strength: PasswordStrength, barIndex: number): string {
  const colorMap = {
    weak: 'bg-red-500',
    medium: 'bg-yellow-500',
    strong: 'bg-green-500',
  };

  const strengthLevels = {
    weak: 1,
    medium: 2,
    strong: 3,
  };

  if (barIndex < strengthLevels[strength]) {
    return colorMap[strength];
  }

  return 'bg-gray-200';
}

/**
 * Get text color class for strength level
 */
function getTextColor(strength: PasswordStrength): string {
  const colorMap = {
    weak: 'text-red-600',
    medium: 'text-yellow-600',
    strong: 'text-green-600',
  };
  return colorMap[strength];
}

/**
 * Password Strength Indicator
 * 
 * Shows a 3-bar visual indicator of password strength:
 * - Red (weak): 1 bar filled
 * - Yellow (medium): 2 bars filled
 * - Green (strong): 3 bars filled
 * 
 * @example
 * ```tsx
 * <PasswordStrengthIndicator password={formData.password} />
 * ```
 */
export function PasswordStrengthIndicator({ 
  password, 
  className = '' 
}: PasswordStrengthIndicatorProps) {
  const { strength, message } = getPasswordStrength(password);

  // Don't show anything if password is empty
  if (!password) {
    return null;
  }

  return (
    <div className={`mt-2 ${className}`}>
      {/* Strength Bars */}
      <div className="flex gap-1" role="progressbar" aria-valuenow={strength === 'weak' ? 33 : strength === 'medium' ? 66 : 100} aria-valuemin={0} aria-valuemax={100} aria-label="Password strength">
        <div className={`h-1 flex-1 rounded transition-colors ${getStrengthColor(strength, 0)}`} />
        <div className={`h-1 flex-1 rounded transition-colors ${getStrengthColor(strength, 1)}`} />
        <div className={`h-1 flex-1 rounded transition-colors ${getStrengthColor(strength, 2)}`} />
      </div>
      
      {/* Strength Message */}
      {message && (
        <p className={`mt-1 text-xs ${getTextColor(strength)}`}>
          {message}
        </p>
      )}
    </div>
  );
}
